import { Fragment, useCallback, useEffect, useState } from "react";
import { api } from "../api";
import { num, pct, rupee, signClass } from "../format";
import { useAppearance } from "../appearanceContext";
import { SERIES_DARK, SERIES_LIGHT } from "../chartMath";
import { bucketBy, groupBySymbol } from "../positionGroups";
import type { PositionGroup } from "../positionGroups";
import type { PositionsTableSettings } from "../types";
import type { OpenPosition, PositionsResponse, Rule, StrategyOption, Trade } from "../types";
import { Breakdown } from "./Breakdown";
import { ColumnPicker } from "./ColumnPicker";
import { Tranches } from "./Tranches";
import { PositionChart } from "./PositionChart";
import { TradeCard } from "./TradeCard";

/** The three ways of looking at the same positions. One at a time: stacking
 *  the breakdown above the table made the tab a long scroll on a phone. */
type View = "list" | "charts" | "breakdown";

/**
 * Open positions, marked to market.
 *
 * The columns are the ones that matter while a trade is live: how much of the
 * book it occupies, where the stop sits, what that risks, and how much of that
 * risk has been earned back. Prices come from the cache, so this is cheap to
 * revisit; a refresh forces a re-fetch.
 */
/** The sortable columns, in the order the table lays them out. */
/**
 * The columns, in order. `symbol` is not listed because it is never hidden —
 * a row with no name is not a row.
 */
const COLUMNS = [
  { key: "qty", label: "Qty" },
  { key: "avg_entry", label: "Avg Buy" },
  { key: "cost_basis", label: "Invested" },
  { key: "last_price", label: "LTP" },
  { key: "day_change_pct", label: "Day %" },
  { key: "day_pnl", label: "Day's P&L" },
  { key: "position_value", label: "Current Value" },
  { key: "unrealized_pnl", label: "Unrealized ₹" },
  { key: "unrealized_pct", label: "Unrealized %" },
  { key: "sizing_pct", label: "Sizing %" },
  { key: "days_held", label: "Days Held" },
  { key: "sl_pct", label: "SL %" },
  { key: "risk_pct", label: "Initial Risk %" },
  { key: "open_risk_pct", label: "Open Risk %" },
  { key: "mae_pct", label: "MAE %" },
  { key: "mfe_pct", label: "MFE %" },
  { key: "given_back_pct", label: "Given Back %" },
  { key: "r_multiple", label: "R Multiples" },
  { key: "portfolio_gain_pct", label: "Portfolio Gain %" },
] as const;

const GROUPINGS = [
  { key: "none", label: "No grouping" },
  { key: "sector", label: "Sector" },
  { key: "industry", label: "Industry" },
  { key: "basic_industry", label: "Basic Industry" },
] as const;

type SortKey = (typeof COLUMNS)[number]["key"] | "tradebook" | "symbol";

export function Positions({
  tradebookId,
  rules,
  strategies,
  multipleBooks,
  onJournalSaved,
}: {
  tradebookId: number;
  rules: Rule[];
  strategies: StrategyOption[];
  /** Hides the scope selector when there is nothing to combine. */
  multipleBooks: boolean;
  onJournalSaved: (trade: Trade) => void;
}) {
  const { appearance } = useAppearance();
  const dark =
    appearance.theme === "dark" ||
    (appearance.theme === "system" && document.documentElement.dataset.theme === "dark");
  const [allBooks, setAllBooks] = useState(false);
  const scope: number | "all" = allBooks ? "all" : tradebookId;
  // Entry order is the default: it is the one ordering that is not an opinion.
  const [sort, setSort] = useState<{ key: SortKey | null; desc: boolean }>({
    key: null,
    desc: false,
  });

  const toggleSort = (key: SortKey) =>
    setSort((current) =>
      current.key === key
        ? // Third click clears it rather than cycling forever between two
          // orderings you did not ask for.
          current.desc
          ? { key: null, desc: false }
          : { key, desc: true }
        : { key, desc: false },
    );
  const [view, setView] = useState<View>("list");
  const [data, setData] = useState<PositionsResponse | null>(null);
  const [search, setSearch] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // The row whose full detail is open, and the trade behind it. The positions
  // response is a summary; the card needs the trade itself, fetched on demand
  // rather than eagerly for every row.
  const [openId, setOpenId] = useState<number | null>(null);
  const [detail, setDetail] = useState<Trade | null>(null);
  // Which merged symbol is showing its per-book holdings.
  const [expanded, setExpanded] = useState<string | null>(null);
  // Which columns are hidden and how rows are grouped, kept on the server so
  // the layout is the same on the phone.
  const [layout, setLayout] = useState<PositionsTableSettings>({
    hidden: [],
    group_by: "none",
  });

  useEffect(() => {
    api.positionsTable().then(setLayout).catch(() => {});
  }, []);

  const saveLayout = (next: PositionsTableSettings) => {
    setLayout(next);
    api.savePositionsTable(next).catch(() => {});
  };

  const shown = COLUMNS.filter((column) => !layout.hidden.includes(column.key));
  // The same fixed, validated order the donuts use, so a sector is the same
  // colour whichever view you are looking at. Past the eighth it repeats
  // rather than inventing a hue no one could tell from an existing one.
  const palette = dark ? SERIES_DARK : SERIES_LIGHT;
  const bucketColour = (index: number) => palette[index % palette.length];
  // Which classification buckets are rolled up. Keyed by name rather than
  // index so a re-sort does not collapse a different one.
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [detailError, setDetailError] = useState<string | null>(null);

  const load = useCallback(
    async (refresh = false) => {
      setLoading(true);
      setError(null);
      try {
        setData(await api.positions(scope, refresh));
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setLoading(false);
      }
    },
    [scope],
  );

  useEffect(() => {
    load();
  }, [load]);

  // Switching books must not leave another book's trade open underneath.
  useEffect(() => {
    setOpenId(null);
    setDetail(null);
  }, [scope]);

  const toggleRow = async (tradeId: number) => {
    if (openId === tradeId) {
      setOpenId(null);
      setDetail(null);
      return;
    }
    setOpenId(tradeId);
    setDetail(null);
    setDetailError(null);
    try {
      setDetail(await api.trade(tradeId));
    } catch (e) {
      setDetailError((e as Error).message);
    }
  };

  /** The expanded trade card, which any row kind can open beneath itself. */
  const detailFor = (tradeId: number) => {
    const row = data?.positions.find((p) => p.trade_id === tradeId);
    return (
    <>
      {detailError && <div className="banner error">{detailError}</div>}
      {!detail && !detailError && <div className="hint">Loading trade…</div>}
      {detail && detail.id === tradeId && (
        <TradeCard
          trade={detail}
          rules={rules}
          strategies={strategies}
          defaultExpanded
          onChange={journalSaved}
          onDelete={async (id) => {
            await api.deleteTrade(id);
            setOpenId(null);
            setDetail(null);
            load();
          }}
        />
      )}
      {detail && detail.id === tradeId && row && row.lots.length > 1 && (
        <div className="card">
          <Tranches
            tradeId={tradeId}
            lots={row.lots}
            positionStop={row.sl_price}
            onSaved={() => load()}
          />
        </div>
      )}
    </>
    );
  };

  /** A journal edit here changes the stop, so the risk columns must re-derive. */
  const journalSaved = (updated: Trade) => {
    setDetail(updated);
    onJournalSaved(updated);
    load();
  };

  if (loading && !data) return <div className="muted">Fetching prices…</div>;
  if (error) return <div className="banner error">{error}</div>;
  if (!data) return null;

  const filtered = data.positions.filter((p) =>
    search ? p.symbol.includes(search.toUpperCase()) : true,
  );

  const rows = sort.key === null ? filtered : sortRows(filtered, sort.key, sort.desc);
  // Merged only in the combined view — within one book a symbol is one row
  // already, and grouping would add a disclosure that never opens.
  const ungrouped = groupBySymbol(filtered);
  const groups =
    sort.key === null ? ungrouped : sortRows(ungrouped, sort.key, sort.desc);

  // Null when nothing is grouped, which keeps the ungrouped path untouched.
  const buckets =
    layout.group_by === "none"
      ? null
      : bucketBy(
          (allBooks ? groups : rows) as (OpenPosition | PositionGroup)[],
          (row) =>
            ((row as unknown as Record<string, unknown>)[layout.group_by] as
              | string
              | null) ??
            "Unclassified",
        );

  const renderMerged = (group: PositionGroup) => (
                <Fragment key={group.symbol}>
                  <tr
                    className={`clickable${expanded === group.symbol ? " is-open" : ""}`}
                    onClick={() =>
                      group.books > 1
                        ? setExpanded((current) =>
                            current === group.symbol ? null : group.symbol,
                          )
                        : toggleRow(group.legs[0].trade_id)
                    }
                  >
                    <td className="muted nowrap">
                      {group.books > 1 ? `${group.books} books` : group.legs[0].tradebook}
                    </td>
                    <Cells
                      shown={shown}
                      row={group}
                      mtf={group.mtf}
                      open={
                        group.books > 1
                          ? expanded === group.symbol
                          : openId === group.legs[0].trade_id
                      }
                    />
                  </tr>

                  {group.books > 1 &&
                    expanded === group.symbol &&
                    group.legs.map((leg) => (
                      <Fragment key={leg.trade_id}>
                        <tr
                          className={`clickable leg-row${
                            openId === leg.trade_id ? " is-open" : ""
                          }`}
                          onClick={() => toggleRow(leg.trade_id)}
                        >
                          <td className="muted nowrap">{leg.tradebook}</td>
                          <Cells
                            shown={shown}
                            row={leg}
                            mtf={leg.product === "mtf"}
                            open={openId === leg.trade_id}
                            indent
                          />
                        </tr>
                        {openId === leg.trade_id && (
                          <tr className="detail-row">
                            <td colSpan={shown.length + (allBooks ? 2 : 1)}>{detailFor(leg.trade_id)}</td>
                          </tr>
                        )}
                      </Fragment>
                    ))}

                  {group.books === 1 && openId === group.legs[0].trade_id && (
                    <tr className="detail-row">
                      <td colSpan={shown.length + (allBooks ? 2 : 1)}>{detailFor(group.legs[0].trade_id)}</td>
                    </tr>
                  )}
                </Fragment>
  );

  const renderRow = (p: OpenPosition) => (
                <Fragment key={p.trade_id}>
                  <tr
                    className={`clickable${openId === p.trade_id ? " is-open" : ""}`}
                    onClick={() => toggleRow(p.trade_id)}
                  >
                    <Cells
                      shown={shown}
                      row={p}
                      mtf={p.product === "mtf"}
                      open={openId === p.trade_id}
                    />
                  </tr>
                  {openId === p.trade_id && (
                    <tr className="detail-row">
                      <td colSpan={shown.length + 1}>{detailFor(p.trade_id)}</td>
                    </tr>
                  )}
                </Fragment>
  );

  return (
    <>
      <div className="toolbar">
        <div className="field">
          <label>Search Symbol</label>
          <input
            value={search}
            placeholder="Search symbol…"
            onChange={(e) => setSearch(e.target.value.toUpperCase())}
          />
        </div>

        {multipleBooks && (
          <div className="field">
            <label>Books</label>
            <select
              value={allBooks ? "all" : "one"}
              onChange={(e) => setAllBooks(e.target.value === "all")}
            >
              <option value="one">Selected book only</option>
              <option value="all">All accounts combined</option>
            </select>
          </div>
        )}

        <div className="tabs" style={{ margin: 0 }}>
          <button className={view === "list" ? "active" : ""} onClick={() => setView("list")}>
            List View
          </button>
          <button className={view === "charts" ? "active" : ""} onClick={() => setView("charts")}>
            Charts
          </button>
          <button
            className={view === "breakdown" ? "active" : ""}
            onClick={() => setView("breakdown")}
          >
            Portfolio Breakdown
          </button>
        </div>

        {view === "list" && (
          <>
            <div className="field">
              <label>Group By</label>
              <select
                value={layout.group_by}
                onChange={(e) =>
                  saveLayout({
                    ...layout,
                    group_by: e.target.value as PositionsTableSettings["group_by"],
                  })
                }
              >
                {GROUPINGS.map((g) => (
                  <option key={g.key} value={g.key}>
                    {g.label}
                  </option>
                ))}
              </select>
            </div>
            <ColumnPicker
              choices={COLUMNS}
              hidden={layout.hidden}
              onChange={(hidden) => saveLayout({ ...layout, hidden })}
            />
          </>
        )}

        <div className="spacer" />
        <button onClick={() => load(true)} disabled={loading}>
          {loading ? "Refreshing…" : "↻ Refresh prices"}
        </button>
      </div>

      <div className="card">
        <div className="stat-strip" style={{ border: "none", paddingBottom: 0 }}>
          <Stat k="Capital" v={rupee(data.capital_base, 0)} />
          <Stat
            k="Deployed"
            v={`${pct(data.deployed_pct)} (${rupee(data.invested, 0)})`}
            title="Cost of everything currently held, against the book's capital"
          />
          <Stat
            k="MTF Exposure"
            v={
              data.mtf_positions === 0
                ? "—"
                : `${pct(data.mtf_pct)} (${rupee(data.mtf_exposure, 0)})`
            }
            cls={data.mtf_positions > 0 ? "warn" : undefined}
            title={
              data.mtf_positions === 0
                ? "Nothing margin funded. Mark a position as MTF on its detail card."
                : `${data.mtf_positions} margin-funded position${
                    data.mtf_positions === 1 ? "" : "s"
                  }, at cost, against the book's capital`
            }
          />
          <Stat k="Net Risk %" v={pct(data.net_risk_pct)} />
          <Stat
            k="Net Unrealized P/L"
            v={`${
              data.net_unrealized_cost_pct === null ? "—" : pct(data.net_unrealized_cost_pct)
            } (${rupee(data.net_unrealized_pnl, 0)})`}
            cls={signClass(data.net_unrealized_pnl)}
            title="Return on the money actually deployed"
          />
          <Stat
            k="Portfolio Impact"
            v={pct(data.net_unrealized_pct)}
            cls={signClass(data.net_unrealized_pnl)}
            title="The same rupees against the whole book — what the Portfolio Gain % column adds up to"
          />
          <Stat
            k="Final Total Capital"
            v={rupee(data.final_total_capital, 0)}
            cls={signClass(data.net_unrealized_pnl)}
          />
          <Stat k="Positions" v={String(data.positions.length)} />
        </div>
      </div>

      {data.unpriced > 0 && (
        <div className="banner warn">
          {data.unpriced} position{data.unpriced === 1 ? "" : "s"} could not be priced — the
          symbol may be delisted, renamed, or unavailable from the data source. Those rows show
          no unrealized figures rather than a guess, and are left out of the totals above.
        </div>
      )}

      {rows.length === 0 ? (
        <div className="card empty">
          <h3>No open positions</h3>
          <p className="muted">Positions you're still holding will appear here.</p>
        </div>
      ) : view === "list" ? (
        <div className="card" style={{ overflowX: "auto" }}>
          <table className="legs">
            <thead>
              <tr>
                {allBooks && (
                  <SortHeader
                    label="Book"
                    sortKey="tradebook"
                    sort={sort}
                    onSort={toggleSort}
                  />
                )}
                <SortHeader
                  label="Symbol"
                  sortKey="symbol"
                  sort={sort}
                  onSort={toggleSort}
                />
                {shown.map((column) => (
                  <SortHeader
                    key={column.key}
                    label={column.label}
                    sortKey={column.key}
                    sort={sort}
                    onSort={toggleSort}
                  />
                ))}
              </tr>
            </thead>
            <tbody>
              {buckets === null
                ? allBooks
                  ? groups.map(renderMerged)
                  : rows.map(renderRow)
                : buckets.map((bucket, index) => (
                    <Fragment key={bucket.key}>
                      <tr
                        className="bucket-row clickable"
                        onClick={() =>
                          setCollapsed((current) => {
                            const next = new Set(current);
                            if (!next.delete(bucket.key)) next.add(bucket.key);
                            return next;
                          })
                        }
                      >
                        <td colSpan={allBooks ? 2 : 1}>
                          <span className="disclosure">
                            {collapsed.has(bucket.key) ? "\u25b8" : "\u25be"}
                          </span>
                          <span
                            className="bucket-chip"
                            style={{ background: bucketColour(index) }}
                          />
                          <strong style={{ color: bucketColour(index) }}>{bucket.key}</strong>
                          <span className="muted"> · {bucket.rows.length}</span>
                        </td>
                        <td colSpan={shown.length} className="bucket-totals">
                          {pct(bucket.share, 1)} of holdings · {rupee(bucket.value, 0)}
                          {bucket.unrealized !== null && (
                            <span className={signClass(bucket.unrealized)}>
                              {" "}
                              · {rupee(bucket.unrealized, 0)}
                            </span>
                          )}
                        </td>
                      </tr>
                      {!collapsed.has(bucket.key) &&
                        (allBooks
                          ? (bucket.rows as PositionGroup[]).map(renderMerged)
                          : (bucket.rows as OpenPosition[]).map(renderRow))}
                    </Fragment>
                  ))}
            </tbody>
          </table>
          <div className="hint">
            {allBooks &&
              "A symbol held in several accounts is merged into one row; open it to see " +
                "what each book holds. "}
            {data.priced_at && (
              <>
                Prices from {new Date(data.priced_at).toLocaleTimeString("en-IN", {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
                , refreshed hourly while the market is open — delayed, not live.{" "}
              </>
            )}
            Click any column heading to sort by it. Click any row for that position's chart, stop, rules and notes. Unrealized P&amp;L
            may differ slightly from your broker's figure — closing prices come
            from a public data source, and it does not deduct exit costs.
          </div>
        </div>
      ) : view === "charts" ? (
        <div className="chart-grid">
          {rows.map((p) => (
            <PositionChart key={p.trade_id} tradeId={p.trade_id} symbol={p.symbol} />
          ))}
        </div>
      ) : (
        <Breakdown positions={rows} />
      )}
    </>
  );
}

/** The columns after Book, identical whether the row is one holding or several. */
function Cells({
  row,
  mtf,
  open,
  shown,
  indent = false,
}: {
  row: OpenPosition | PositionGroup;
  mtf: boolean;
  open: boolean;
  shown: readonly { key: string; label: string }[];
  indent?: boolean;
}) {
  const value = (key: string) => (row as unknown as Record<string, unknown>)[key] ?? null;

  const cell = (key: string) => {
    switch (key) {
      case "last_price":
        return <td key={key}>{value(key) === null ? "—" : num(value(key) as number)}</td>;
      case "day_change_pct":
        return (
          <td key={key} className={signClass(value(key) as number | null)}>
            {value(key) === null ? "—" : pct(value(key) as number, 2)}
          </td>
        );
      case "unrealized_pnl":
      case "day_pnl":
        return (
          <td key={key} className={signClass(value(key) as number | null)}>
            {value(key) === null ? "—" : num(value(key) as number)}
          </td>
        );
      case "unrealized_pct":
        return (
          <td key={key} className={signClass(row.unrealized_pnl)}>
            {row.unrealized_pct === null ? "—" : pct(row.unrealized_pct, 2)}
          </td>
        );
      case "qty":
        return <td key={key}>{row.qty}</td>;
      case "mae_pct":
        return <td key={key} className="neg">{row.mae_pct === null ? "—" : num(row.mae_pct)}</td>;
      case "mfe_pct":
        return <td key={key} className="pos">{row.mfe_pct === null ? "—" : num(row.mfe_pct)}</td>;
      case "given_back_pct":
        // Not a loss, so not red: it is how much of the best is no longer there.
        return (
          <td key={key} className={row.given_back_pct && row.given_back_pct > 5 ? "warn" : ""}>
            {row.given_back_pct === null ? "—" : num(row.given_back_pct)}
          </td>
        );
      case "days_held":
        return <td key={key}>{row.days_held}</td>;
      case "r_multiple":
      case "portfolio_gain_pct":
        return (
          <td key={key} className={signClass(value(key) as number | null)}>
            {value(key) === null ? "—" : num(value(key) as number)}
          </td>
        );
      default:
        return <td key={key}>{value(key) === null ? "—" : num(value(key) as number)}</td>;
    }
  };

  return (
    <>
      <td className={indent ? "leg-symbol" : undefined}>
        <span className="disclosure">{open ? "▾" : "▸"}</span>
        <strong>{row.symbol}</strong>
        {row.stale_price && (
          <span className="badge muted" title="Price is out of date">
            stale
          </span>
        )}
        {mtf && (
          <span className="badge mtf" title="Margin funded">
            MTF
          </span>
        )}
        {"averaging_down" in row && row.averaging_down && (
          <span
            className="badge warn-badge"
            title="A parcel was added below the average of the ones before it"
          >
            avg down
          </span>
        )}
        {"lots" in row && row.lots.length > 1 && (
          <span className="badge muted" title="Built in more than one entry">
            {row.lots.length} tranches
          </span>
        )}
        {row.anomaly && (
          <span className="badge muted" title={row.anomaly}>
            flagged
          </span>
        )}
      </td>
      {shown.map((column) => cell(column.key))}
    </>
  );
}

function SortHeader({
  label,
  sortKey,
  sort,
  onSort,
}: {
  label: string;
  sortKey: SortKey;
  sort: { key: SortKey | null; desc: boolean };
  onSort: (key: SortKey) => void;
}) {
  const active = sort.key === sortKey;
  return (
    <th
      className="sortable"
      aria-sort={active ? (sort.desc ? "descending" : "ascending") : "none"}
      onClick={() => onSort(sortKey)}
    >
      {label}
      <span className="sort-caret">{active ? (sort.desc ? "▾" : "▴") : ""}</span>
    </th>
  );
}

/**
 * Sorts a copy, never the array it was given.
 *
 * Positions with no value for the column — no stop, so no risk — always sink
 * to the bottom whichever way the sort runs. A missing number is not a small
 * one, and letting it sort as zero would put unstopped trades at the top of a
 * "lowest risk" list.
 */
function sortRows<T extends OpenPosition | PositionGroup>(
  rows: T[],
  key: SortKey,
  desc: boolean,
): T[] {
  const direction = desc ? -1 : 1;
  return [...rows].sort((a, b) => {
    // A merged row has no single book, so sorting by Book leaves it in place.
    const read = (row: T) => ((row as unknown as Record<string, unknown>)[key] ?? null) as
      | string
      | number
      | null;
    const left = read(a);
    const right = read(b);
    if (left === null && right === null) return 0;
    if (left === null) return 1;
    if (right === null) return -1;
    if (typeof left === "string" && typeof right === "string") {
      return left.localeCompare(right) * direction;
    }
    return ((left as number) - (right as number)) * direction;
  });
}

function Stat({
  k,
  v,
  cls,
  title,
}: {
  k: string;
  v: string;
  cls?: string;
  title?: string;
}) {
  return (
    <div className="stat" title={title}>
      <div className="k">{k}</div>
      <div className={`v ${cls ?? ""}`}>{v}</div>
    </div>
  );
}
